import { useEffect } from "react";

import { useEntityRecordsLinkedEntitiesBatchBySourceExternalIdGetV2 } from "@btnet/services-btnet/handlers/entity-records/entity-records";
import type { EntityType } from "@btnet/services-btnet/handlers/models/entityType";

import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { getEntityDisplayData } from "./EntityLinkRoutes.utilities";
import {
    type IEntityLinkSelectorProps,
    type IEntitySelectItemExtraData,
} from "./EntityLinkSelector.types";
import { EntityLinkSelectorEdit } from "./EntityLinkSelectorEdit";
import { EntityLinkSelectorReadOnly } from "./EntityLinkSelectorReadOnly";

/**
 * Generic component for linking entities to other entities
 * Supports both read-only display mode and edit mode with add/edit capabilities
 * Acts as a router between EntityLinkSelectorEdit and EntityLinkSelectorReadOnly
 *
 * @example
 * <EntityLinkSelector
 *     sourceExternalId={purchaseOrder.externalId}
 *     sourceEntityType={EntityType.PurchaseOrder}
 *     targetEntityType={EntityType.Specification}
 *     jobId={jobId}
 *     canAdd={canAdd}
 *     renderDetail={renderSpecificationDetail}
 * />
 */
export const EntityLinkSelector = <FormValues, ExtraData extends IEntitySelectItemExtraData>(
    props: IEntityLinkSelectorProps<FormValues, ExtraData>,
) => {
    const {
        sourceExternalId,
        sourceEntityType,
        targetEntityType,
        jobId,
        isReadOnly = false,
        canAdd = false,
        canEdit = false,
        fieldPath = "entities" as KeyOfOrString<FormValues>,
        useBds = true,
        renderDetail,
        customItemRender,
        renderStatus,
        helperText,
        dropdownClassName,
        successMessage,
        onLinkedCountChange,
        queryKeyEntityTypes,
        onPersistLinksOverride,
    } = props;

    // Get display data from registry
    const displayData = getEntityDisplayData(targetEntityType);
    const entityNamePlural = displayData.namePlural.toLowerCase();
    const entityName = displayData.name.toLowerCase();

    // Default to [targetEntityType] so the cache key is correct when no override is provided
    const resolvedQueryKeyEntityTypes: EntityType[] = queryKeyEntityTypes ?? [targetEntityType];

    // Fetch currently linked entities via batch endpoint (shared cache with SpecificationsLinkButton/SpecificationsPanelLayout)
    // Cache is updated directly by EntityLinkSelectorEdit for optimistic updates
    const { data: batchData, isLoading: isLoadingLinked } =
        useEntityRecordsLinkedEntitiesBatchBySourceExternalIdGetV2(
            sourceExternalId,
            { targetEntityTypes: resolvedQueryKeyEntityTypes },
            { query: { refetchOnMount: false } },
        );

    // Derive linked entities for this specific type from the grouped batch response
    const linkedEntities =
        batchData?.data.entitiesByType.find((g) => g.entityType === targetEntityType)
            ?.entityRecords ?? [];
    const linkedIds = linkedEntities.map((e) => e.id);

    useEffect(() => {
        onLinkedCountChange?.(linkedEntities.length);
    }, [linkedEntities.length, onLinkedCountChange]);

    const fieldPathString = String(fieldPath);

    // Read-only mode: Show only tags inline
    if (isReadOnly) {
        if (isLoadingLinked) {
            return null;
        }
        return (
            <EntityLinkSelectorReadOnly
                linkedEntities={linkedEntities}
                targetEntityType={targetEntityType}
                jobId={jobId}
                fieldPath={fieldPathString}
                entityNamePlural={entityNamePlural}
                sourceExternalId={sourceExternalId}
                queryKeyEntityTypes={resolvedQueryKeyEntityTypes}
                renderStatus={renderStatus}
            />
        );
    }

    return (
        <EntityLinkSelectorEdit
            sourceExternalId={sourceExternalId}
            sourceEntityType={sourceEntityType}
            targetEntityType={targetEntityType}
            jobId={jobId}
            linkedIds={linkedIds}
            isLoadingLinked={isLoadingLinked}
            canAdd={canAdd}
            canEdit={canEdit}
            fieldPath={fieldPath}
            useBds={useBds}
            renderDetail={renderDetail}
            entityNamePlural={entityNamePlural}
            entityName={entityName}
            customItemRender={customItemRender}
            helperText={helperText}
            dropdownClassName={dropdownClassName}
            successMessage={successMessage}
            queryKeyEntityTypes={resolvedQueryKeyEntityTypes}
            onPersistLinksOverride={onPersistLinksOverride}
        />
    );
};
